import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { List as MUIList, ListItemText as _ListItemText, MenuItem as _MenuItem, Typography } from '@material-ui/core';
import { List } from 'immutable';
import { ClassAttributes, createElement, SyntheticEvent } from 'react';
import { FormattedMessage } from 'react-intl';

import FacebookAdAccount, { MyRole } from '../../../entity/FacebookAdAccount';
import theme from '../../../theme';
import { SPACING_REGULAR } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import mq from '../../styleUtility/mediaquery';

interface Props {
  facebookAdAccounts: List<FacebookAdAccount>;
  selected: FacebookAdAccount | null;
  onClick: (e: SyntheticEvent<HTMLElement>, selected: FacebookAdAccount) => void;
  className?: string;
}

const FacebookAdAccountList = ({
  facebookAdAccounts,
  selected,
  onClick,
  className,
}: ClassAttributes<HTMLElement> & Props) => {
  const selectedListItemPrimaryStyle = css({
    color: theme.palette.primary.main,
  }).toString();

  const selectedListItemSecondaryStyle = css({
    color: theme.palette.primary.light,
  }).toString();

  const accounts = facebookAdAccounts.map((adAccount) => {
    const disabled = adAccount.myRole === MyRole.REPORTS_ONLY;

    const caption = disabled && (
      <Typography variant="caption">
        <FormattedMessage id="facebook_ad_account.unauthorized" />
      </Typography>
    );

    const isSelected = selected ? selected.id === adAccount.id : false;

    const AccountName = isSelected ? (
      <ListItemText
        primary={adAccount.name}
        secondary={adAccount.business.name}
        classes={{
          primary: selectedListItemPrimaryStyle,
          secondary: selectedListItemSecondaryStyle,
        }}
      />
    ) : (
      <ListItemText primary={adAccount.name} secondary={adAccount.business.name} />
    );

    return (
      <MenuItem selected={isSelected} key={adAccount.id} disabled={disabled} onClick={(e) => onClick(e, adAccount)}>
        {AccountName}
        {caption}
      </MenuItem>
    );
  });

  return <Root className={className}>{accounts}</Root>;
};

const Root = styled(MUIList)({
  height: '100%',
  maxHeight: '635px',
  overflowY: 'scroll',
  border: `1px solid ${theme.palette.grey['200']}`,
  [mq('sp')]: {
    maxHeight: 'none',
    overflowY: 'auto',
  },
});

const MenuItem = styled(_MenuItem)(
  flexibleLayoutContainer({
    direction: 'row',
    wrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
  }),
  {
    height: 48,
    [mq('sp')]: {
      height: 'auto',
    },
  },
);

const ListItemText = styled(_ListItemText)({
  marginRight: SPACING_REGULAR,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  [mq('sp')]: {
    whiteSpace: 'initial',
    wordBreak: 'break-word',
    marginRight: 0,
    paddingRight: 0,
    '& > span': {
      lineHeight: 1.1,
    },
    '& > p': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
});

export default FacebookAdAccountList;
